/**
 * DetailHeroScene - Unified hero component for content detail pages
 * Ports Flutter's TvDetailHero structure to React/WebTV.
 * 
 * Used by: movie/filmPageContent.js, series/filmPageContent.jsx
 * 
 * Features:
 * - Background image with vertical/horizontal overlays
 * - Title with text shadows
 * - Metadata: year, country, duration, age rating
 * - IMDB rating and like percentage
 * - Genre chips with focus navigation
 * - Glassmorphism action buttons
 * - Video logo positioning
 */
import React, { useCallback } from 'react';
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { useNavigate } from 'react-router-dom';
import {
    HeroContainer,
    HeroBackground,
    VerticalOverlay,
    HorizontalOverlay,
    ContentWrapper,
    ContentInner,
    ContentCell,
    TitleText,
    SubtitleText,
    MetadataRow,
    MetadataItem,
    DurationWrapper,
    ClockIcon,
    AgeBadgeWrapper,
    RatingRow,
    RatingItem,
    ImdbLogo,
    LikeIcon,
    RatingText,
    DescriptionText,
    GenreChipsContainer,
    GenreChip,
    ActionButtonsRow,
    VideoLogoContainer,
    TurkishBadge,
    ButtonSpacer,
    IconActionsContainer,
} from './DetailHeroScene.styles';
import { GlassmorphismButton, GlassmorphismIconButton } from './GlassmorphismButton';
import OptimizedImage from './OptimizedImage';
import { getOptimizedImageForType, tagsGuide } from '../../utils/cms.utils';
import GetAgeRangeComponent from '../../utils/ageRange.util';
import { useInputMode } from '../../utils/hooks/useInputMode';

// Genre chip with focus support
const GenreChipButton = ({ item, onEnterPress, onFocus }) => {
    const { ref, focused, focusSelf } = useFocusable({
        onEnterPress,
        onFocus,
    });

    const { setMouseFocus } = useInputMode();
    const handleMouseEnter = useCallback(() => {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    return (
        <GenreChip
            ref={ref}
            $focused={focused}
            onClick={onEnterPress}
            onMouseEnter={handleMouseEnter}
            type="button"
        >
            {item.name}
        </GenreChip>
    );
};

/**
 * DetailHeroScene Component
 */
const DetailHeroScene = ({
    content,
    onPlay,
    onTrailer,
    onBookmark,
    onLike,
    onDislike,
    playLabel = 'تماشا کن',
    isBookmarked = false,
    isLiked = false,
    isDisliked = false,
    isPlayLoading = false,
    isSeries = false,
    onRowFocus,
    renderButtons, // Optional: custom button rendering function
}) => {
    const navigate = useNavigate();

    // Extract content data
    const title = content?.title || '';
    const titleEn = content?.titleEn || '';
    const description = content?.description || '';
    const year = content?.year;
    const country = content?.country?.[0];
    const time = content?.time;
    const imdbRate = content?.imdb_rate || content?.imdbRate;
    const ageRange = content?.ageRange;
    const genres = content?.genreInfo || [];
    const tags = content?.tags || [];
    const trailer = content?.trailer;

    // Check for Turkish content
    const isTurkish = tags?.[0] && tagsGuide[tags[0]] === 'TURKISH_NATIVE';

    const backgroundImageData = getOptimizedImageForType('horizontalCover', content?.contentPhotos, 'TV', { raw: true });
    const videoLogoData = getOptimizedImageForType('videoLogo', content?.contentPhotos, 'TV', { raw: true });

    // Truncate description (max 150 chars like Flutter)
    const truncatedDescription = description.length > 150
        ? description.substring(0, 150) + '...'
        : description;

    // Calculate like percentage from IMDB rate
    const likePercentage = imdbRate ? Math.round(imdbRate * 10) : null;

    return (
        <HeroContainer>
            {/* Background layers with LQIP support - using OptimizedImage for proper lazy loading */}
            <OptimizedImage
                src={backgroundImageData.src}
                alt={title}
                loading="eager"
                fetchPriority="high"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: -1,
                    right: -1,
                    width: 'calc(100% + 2px)',
                    height: '90vh',
                    objectFit: 'cover',
                    zIndex: 1,
                }}
            />
            <VerticalOverlay />
            <HorizontalOverlay />

            {/* Video logo - bottom left with LQIP */}
            {videoLogoData.src && (
                <VideoLogoContainer>
                    <OptimizedImage
                        src={videoLogoData.src}
                        alt={title}
                        style={{ width: 250, height: 'auto', objectFit: 'contain' }}
                    />
                </VideoLogoContainer>
            )}

            {/* Content */}
            <ContentWrapper>
                <ContentInner>
                    <ContentCell>
                        {/* Turkish badge */}
                        {isTurkish && (
                            <TurkishBadge
                                src="/tags/turkish-alt.svg"
                                alt="ترکی دیلینده"
                            />
                        )}

                        {/* Title */}
                        <TitleText>{title}</TitleText>

                        {/* English title */}
                        {titleEn && <SubtitleText>{titleEn}</SubtitleText>}

                        {/* Metadata row */}
                        <MetadataRow>
                            {year && (
                                <MetadataItem>
                                    {year}{country ? ` | ${country}` : ''}
                                </MetadataItem>
                            )}

                            {time && (
                                <DurationWrapper>
                                    <MetadataItem>{time} دقیقه</MetadataItem>
                                </DurationWrapper>
                            )}

                            {ageRange && (
                                <AgeBadgeWrapper>
                                    {GetAgeRangeComponent(ageRange)}
                                </AgeBadgeWrapper>
                            )}
                        </MetadataRow>

                        {/* IMDB and Like rating */}
                        <RatingRow>
                            {imdbRate && (
                                <RatingItem>
                                    <ImdbLogo src="/svg/imdb.svg" alt="IMDB" />
                                    <RatingText>{imdbRate}</RatingText>
                                </RatingItem>
                            )}

                            {likePercentage && (
                                <RatingItem>
                                    <LikeIcon src="/svg/like-percent.svg" alt="Like" />
                                    <RatingText>{likePercentage}%</RatingText>
                                </RatingItem>
                            )}
                        </RatingRow>

                        {/* Genre chips */}
                        {genres.length > 0 && (
                            <GenreChipsContainer>
                                {genres.map((genre, index) => (
                                    <GenreChipButton
                                        key={genre.slug + index}
                                        item={genre}
                                        onEnterPress={() => navigate(`/genre/${genre.slug}?kind=${content.contentType?.kind}`)}
                                        onFocus={onRowFocus}
                                    />
                                ))}
                            </GenreChipsContainer>
                        )}

                        {/* Description */}
                        {truncatedDescription && (
                            <DescriptionText>{truncatedDescription}</DescriptionText>
                        )}

                        {/* Action buttons row */}
                        <ActionButtonsRow>
                            {/* Custom buttons rendering */}
                            {renderButtons ? (
                                renderButtons()
                            ) : (
                                <>
                                    {/* Play button (hidden for series) */}
                                    {!isSeries && onPlay && (
                                        <GlassmorphismButton
                                            label={playLabel}
                                            icon="▶"
                                            variant="primary"
                                            loading={isPlayLoading}
                                            autofocus={true}
                                            onEnterPress={onPlay}
                                            onFocus={onRowFocus}
                                        />
                                    )}

                                    {/* Preview/Trailer button */}
                                    {trailer && onTrailer && (
                                        <>
                                            {!isSeries && <ButtonSpacer $width="10px" />}
                                            <GlassmorphismButton
                                                label="پیش نمایش"
                                                variant="secondary"
                                                onEnterPress={onTrailer}
                                                onFocus={onRowFocus}
                                            />
                                        </>
                                    )}

                                    {/* Icon buttons on left side */}
                                    <IconActionsContainer>
                                        {/* Like button (movies only) */}
                                        {onLike && !isSeries && (
                                            <GlassmorphismIconButton
                                                icon={<img src={isLiked ? '/svg/thumbs-up-filled.svg' : '/svg/thumbs-up.svg'} alt="Like" width="24" height="24" style={{ filter: 'brightness(0) invert(1)' }} />}
                                                onEnterPress={onLike}
                                                onFocus={onRowFocus}
                                            />
                                        )}

                                        {/* Dislike button (movies only) */}
                                        {onDislike && !isSeries && (
                                            <GlassmorphismIconButton
                                                icon={<img src={isDisliked ? '/svg/thumbs-down-filled.svg' : '/svg/thumbs-down.svg'} alt="Dislike" width="24" height="24" style={{ filter: 'brightness(0) invert(1)' }} />}
                                                onEnterPress={onDislike}
                                                onFocus={onRowFocus}
                                            />
                                        )}

                                        {/* Bookmark button */}
                                        {onBookmark && (
                                            <GlassmorphismIconButton
                                                icon={<img src={isBookmarked ? '/svg/bookmark-filled.svg' : '/svg/bookmark-empty.svg'} alt="Bookmark" width="24" height="24" style={{ filter: 'brightness(0) invert(1)' }} />}
                                                onEnterPress={onBookmark}
                                                onFocus={onRowFocus}
                                            />
                                        )}
                                    </IconActionsContainer>
                                </>
                            )}
                        </ActionButtonsRow>
                    </ContentCell>
                </ContentInner>
            </ContentWrapper>
        </HeroContainer>
    );
};

export default DetailHeroScene;
